import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createStorePerformanceRequest,
  KpiDropdownActions,
  KpiType,
  StorePerformanceInterval,
} from '../../../../../constants';
import {
  CustomerEngagementResponse,
  CustomerEngagmentPerformanceItem,
  useAsyncAction,
  useStores,
  useTransaction,
  useUser,
} from '../../../../../hooks';
import { useCelebrateTarget } from '../../../../../hooks/useCelebrateTarget';
import { Icon, Icons, Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { DropdownMenu, IconWithText } from '../../../../../stories/molecules';
import { ContainerDrawer } from '../../../../../stories/molecules/container-drawer/ContainerDrawer';
import { muiTheme } from '../../../../../theme';
import { homeTranslations, kpiTargetTranslations } from '../../../../../translations';
import { formatValue, getKpiTargetProps, getKpiTargetValue, isFailureResponse } from '../../../../../utils';
import { WidgetMetricTile, WidgetMetricType } from '../../../common/metricTile/WidgetMetricTile';
import { getDefaultStatus } from '../../../common/metricTile/util';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { WidgetMobileLayout } from '../../../common/widgetMobileLayout/WidgetMobileLayout';
import { isMonthlyView } from '../../../util';
import { CustomerEngagementCard } from './customerEngagementCard/CustomerEngagementCard';
import { EngagementOverview } from './engagementOverview/EngagementOverview';
import { EngagementSlider } from './engagementSlider/EngagementSlider';

export type EngagementMetrics = 'memberRecruited' | 'memberIdentification' | 'articleLookups';
const { customerEngagement: customerEngagementranslations, kpiTargets } = homeTranslations;

export type CustomerEngagementTranslation = keyof typeof customerEngagementranslations;

export type CustomerEngagementWidgetProps = {
  currentDate: Date;
  interval: StorePerformanceInterval;
  isToday: boolean;
  isKpiLoading: boolean;
  isKpiError: boolean;
  hasPermissionToEditKpi: boolean;
  handleDropdownDetails: (kpiType: KpiType, action: KpiDropdownActions) => void;
  onTargetAchieved?: (targetValue: string, updatedDate: Date) => void;
};

export type MemberRecruited = {
  recruitment: number;
  inStoreCareApp: number;
  ratio: number;
};

export type MemberIdentification = {
  membersIdentified: number;
  memberPurchases: number;
  conversionRate: number;
};

export type ArticleLookups = {
  lookups: number;
  purchasedArticles: number;
  conversionRate: number;
};

export type CustomerDataType = {
  memberRecruited: MemberRecruited;
  memberIdentification: MemberIdentification;
  articleLookups: ArticleLookups;
};

export const CustomerEngagementWidget: FC<CustomerEngagementWidgetProps> = observer(
  ({
    currentDate,
    interval,
    isToday,
    hasPermissionToEditKpi,
    isKpiLoading,
    isKpiError,
    handleDropdownDetails,
    onTargetAchieved,
  }) => {
    const { t } = useTranslation();
    const {
      get: { kpiTargetsProps },
    } = useUser();
    const { fetchCustomerEngagementData } = useTransaction();
    const { fetchForecastForKpis } = useStores();
    const isMobileView = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

    const target = getKpiTargetValue(kpiTargetsProps, 'dailyCustomerEngagement');
    const isTargetLocked = getKpiTargetProps(kpiTargetsProps, 'dailyCustomerEngagement')?.isLocked ?? false;

    const [isEngagementSliderOpen, setIsEngagementSliderOpen] = useState(false);
    const [totalCustomerEngaged, setTotalCustomerEngaged] = useState(0);
    const [selectedMetric, setSelectedMetric] = useState<EngagementMetrics>('memberRecruited');
    const [customerData, setCustomerData] = useState<CustomerDataType>({
      memberRecruited: {
        recruitment: 0,
        inStoreCareApp: 0,
        ratio: 0,
      },
      memberIdentification: {
        membersIdentified: 0,
        memberPurchases: 0,
        conversionRate: 0,
      },
      articleLookups: {
        lookups: 0,
        purchasedArticles: 0,
        conversionRate: 0,
      },
    });

    const {
      result: customerEngagementData,
      isLoading: customerEngagementLoading,
      isError: customerEngagementError,
      triggerAction: triggerFetchCustomerEngagementAction,
    } = useAsyncAction<CustomerEngagementResponse>();

    const {
      result: customerEngagementForecastData,
      isLoading: customerEngagementForecastLoading,
      isError: customerEngagementForecastError,
      triggerAction: triggerFetchCustomerEngagementForecastAction,
    } = useAsyncAction<CustomerEngagmentPerformanceItem[]>();

    // fetch data
    const fetchData = useCallback(async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });

      const response = await fetchCustomerEngagementData(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      return response.data;
    }, [interval, currentDate]);

    // fetch forecast data
    const fetchForecastData = useCallback(async () => {
      const response = await fetchForecastForKpis<CustomerEngagmentPerformanceItem>('customerEngagement');

      if (isFailureResponse(response)) {
        throw new Error();
      }

      return response.data;
    }, [interval, currentDate]);

    // Calculate engagement metrics
    useEffect(() => {
      if (!customerEngagementData) return;

      const { memberRecruited, memberIdentification, articleLookups } = customerEngagementData;

      const totalEngaged =
        memberRecruited.inStoreCareApp + memberIdentification.membersIdentified + articleLookups.lookups;

      setTotalCustomerEngaged(totalEngaged);
      setCustomerData({
        memberRecruited: {
          recruitment: memberRecruited.recruitment,
          inStoreCareApp: memberRecruited.inStoreCareApp,
          ratio:
            memberRecruited.recruitment === 0
              ? 0
              : (memberRecruited.inStoreCareApp / memberRecruited.recruitment) * 100,
        },
        memberIdentification: {
          membersIdentified: memberIdentification.membersIdentified,
          memberPurchases: memberIdentification.memberPurchases,
          conversionRate:
            memberIdentification.membersIdentified === 0
              ? 0
              : (memberIdentification.memberPurchases / memberIdentification.membersIdentified) * 100,
        },
        articleLookups: {
          lookups: articleLookups.lookups,
          purchasedArticles: articleLookups.purchasedArticles,
          conversionRate:
            articleLookups.lookups === 0 ? 0 : (articleLookups.purchasedArticles / articleLookups.lookups) * 100,
        },
      });
    }, [customerEngagementData]);

    useEffect(() => {
      triggerFetchCustomerEngagementAction(fetchData);
      if (isMonthlyView(interval)) return;
      if (interval === StorePerformanceInterval.DAILY && isToday) {
        triggerFetchCustomerEngagementForecastAction(fetchForecastData);
      }
    }, [currentDate]);

    const customerEngagementPercentage =
      customerEngagementData &&
      (customerEngagementData?.storeTraffic === 0
        ? 0
        : (totalCustomerEngaged / customerEngagementData.storeTraffic) * 100);

    const forecastCustomerEngaged =
      customerEngagementForecastData?.reduce((acc, item) => acc + item.customerEngaged, 0) ?? 0;

    const forecastStoreTraffic =
      customerEngagementForecastData?.reduce((acc, item) => acc + item.storeTraffic, 0) ?? 0;

    const forecastEngagement = forecastCustomerEngaged + totalCustomerEngaged;
    const forecastTraffic = forecastStoreTraffic + (customerEngagementData?.storeTraffic ?? 0);

    const forecastCustomerEngagementPercentage = forecastTraffic !== 0 ? forecastEngagement / forecastTraffic : 0;

    const targetProgressPercent =
      target && target != 0 ? (forecastCustomerEngagementPercentage / target) * 100 : 0;

    const handleSelectedMetric = (name: EngagementMetrics) => {
      setSelectedMetric(name);
      setIsEngagementSliderOpen(true);
    };

    const isPreviousDay = interval === StorePerformanceInterval.WEEKLY || !isToday;
    const isEditKPIDisabled = !hasPermissionToEditKpi || isTargetLocked || isPreviousDay;

    useCelebrateTarget({
      target,
      currentValue: customerEngagementPercentage ?? 0,
      isToday,
      localStorageKey: 'customerEngagementTargetAchieved',
      lastUpdated: customerEngagementData?.lastUpdated ?? currentDate,
      onTargetAchieved: (targetValue, updatedDate) => {
        const formattedTarget = `${targetValue.toFixed(2)}%`;
        if (onTargetAchieved) {
          onTargetAchieved(formattedTarget, updatedDate);
        }
      },
    });

    const menuItems = [
      {
        key: target ? 'editkpi' : 'addkpi',
        disabled: isEditKPIDisabled,
        hasTooltip: isEditKPIDisabled,
        tooltipText: (
          <ColumnFlexContainer padding={[1]} flexWrap="wrap">
            <Typography color="white">
              {!hasPermissionToEditKpi
                ? t(kpiTargets.noPermissionTooltip.key, kpiTargets.noPermissionTooltip.defaultValue)
                : isTargetLocked
                ? t(kpiTargets.lockedTargetTooltip.key, kpiTargets.lockedTargetTooltip.defaultValue)
                : isPreviousDay
                ? t(kpiTargets.previousDayTooltip.key, kpiTargets.previousDayTooltip.defaultValue)
                : t(kpiTargets.disabledTooltip.key, kpiTargets.disabledTooltip.defaultValue)}
            </Typography>
          </ColumnFlexContainer>
        ),
        item: (
          <Typography margin={[0, 2, 0]}>
            {target
              ? t(kpiTargets.editKpi.key, kpiTargets.editKpi.defaultValue)
              : t(kpiTargets.addKpi.key, kpiTargets.addKpi.defaultValue)}
          </Typography>
        ),
      },
    ];

    return (
      <ContainerDrawer
        isOpen={isEngagementSliderOpen}
        onClose={() => setIsEngagementSliderOpen(false)}
        sliderContent={
          <EngagementSlider
            metric={customerData[selectedMetric]}
            totalCustomerEngaged={formatValue(totalCustomerEngaged, {
              options: { minimumFractionDigits: 0, notation: 'compact' },
            })}
            metricName={selectedMetric}
          />
        }
        width="250px"
      >
        <WidgetCardContent
          headerIcon={Icons.PROFILE}
          headerText={t(customerEngagementranslations.title.key, customerEngagementranslations.title.defaultValue)}
          subHeaderText={t(
            customerEngagementranslations.subTitle.key,
            customerEngagementranslations.subTitle.defaultValue,
          )}
          isLoading={customerEngagementLoading || customerEngagementForecastLoading}
          isError={customerEngagementError || customerEngagementForecastError}
          errorText={t(customerEngagementranslations.error.key, customerEngagementranslations.error.defaultValue)}
          toolTipText={t(
            customerEngagementranslations.toolTip.key,
            customerEngagementranslations.toolTip.defaultValue,
          )}
          noDataText={t(
            customerEngagementranslations.nodataText.key,
            customerEngagementranslations.nodataText.defaultValue,
          )}
          isNoData={totalCustomerEngaged === 0}
          target={
            target
              ? `${target.toFixed(2)}%`
              : t(kpiTargetTranslations.notSet.key, kpiTargetTranslations.notSet.defaultValue)
          }
          lastUpdatedDate={customerEngagementData?.lastUpdated}
          headerSideContent={
            <RowFlexContainer alignItems="center">
              <DropdownMenu
                data-testid="kpiDropdown"
                dropdownLabel={''}
                buttonContentProps={{
                  iconOptions: { customIcon: <Icon icon={Icons.MORE} /> },
                }}
                menuItems={menuItems}
                minWidth={'0px'}
                isCompact
                initialSelected={[]}
                onSelect={(selectedItem) => {
                  handleDropdownDetails('dailyCustomerEngagement', selectedItem[0] as KpiDropdownActions);
                }}
                isOnlyIcon
                allowTooltip
              />
            </RowFlexContainer>
          }
          content={
            <ColumnFlexContainer
              data-testid="customer-engagement-widget-wrapper"
              padding={[2, 4, 4]}
              height="100%"
            >
              {target !== null && (
                <RowFlexContainer gap={[2]}>
                  {!isPreviousDay && (
                    <WidgetMetricTile
                      style={{ flex: isMobileView ? 1 : 'unset' }}
                      metricProperties={{
                        type: WidgetMetricType.ForecastMetric,
                        properties: {
                          metricValue: `${Math.trunc(Math.round(forecastCustomerEngagementPercentage))}`,
                          forecastIndicator: getDefaultStatus(targetProgressPercent),
                          metricTooltipText: t(
                            homeTranslations.common.forecastTileTooltip.key,
                            homeTranslations.common.forecastTileTooltip.defaultValue,
                          ),
                        },
                      }}
                    />
                  )}

                  {!isMobileView && (
                    <WidgetMetricTile
                      isLoading={isKpiLoading}
                      isError={isKpiError}
                      metricProperties={{
                        type: WidgetMetricType.RegularMetric,
                        properties: {
                          metricTitle: t(
                            kpiTargetTranslations.kpiProperties.target.title.key,
                            kpiTargetTranslations.kpiProperties.target.title.defaultValue,
                          ),
                          metricValue: `${target.toFixed(2)}%`,
                        },
                      }}
                    />
                  )}
                </RowFlexContainer>
              )}
              {!isMobileView && (
                <RowFlexContainer
                  gap={[4]}
                  data-testid="customer-engagement-desktop-container"
                  padding={[2, 0]}
                  height="100%"
                >
                  <EngagementOverview
                    totalCustomerEngaged={totalCustomerEngaged}
                    storeTraffic={customerEngagementData?.storeTraffic ?? 0}
                    memberRecruited={customerEngagementData?.memberRecruited.inStoreCareApp ?? 0}
                    memberIdentification={customerEngagementData?.memberIdentification.membersIdentified ?? 0}
                    articleLookups={customerEngagementData?.articleLookups.lookups ?? 0}
                  />
                  <CustomerEngagementCard customerData={customerData} />
                </RowFlexContainer>
              )}

              {isMobileView && (
                <WidgetMobileLayout
                  enableFlex
                  metricItems={[
                    {
                      label: t(
                        kpiTargetTranslations.kpiProperties.target.title.key,
                        kpiTargetTranslations.kpiProperties.target.title.defaultValue,
                      ),
                      content: target === null ? `-` : target.toFixed(2),
                      isLoading: isKpiLoading,
                      isError: isKpiError,
                      tooltipText: t(
                        kpiTargetTranslations.kpiProperties.target.tooltip.key,
                        kpiTargetTranslations.kpiProperties.target.tooltip.defaultValue,
                      ),
                    },
                    {
                      label: `${t(
                        customerEngagementranslations.title.key,
                        customerEngagementranslations.title.defaultValue,
                      )} (%)`,
                      content: customerEngagementPercentage ? customerEngagementPercentage.toFixed(2) : '0',
                      tooltipText: t(
                        customerEngagementranslations.customerEngagementPercentageTooltip.key,
                        customerEngagementranslations.customerEngagementPercentageTooltip.defaultValue,
                      ),
                    },
                    {
                      label: `${t(
                        customerEngagementranslations.customerEngaged.key,
                        customerEngagementranslations.customerEngaged.defaultValue,
                      )} (#)`,
                      content: formatValue(totalCustomerEngaged, {
                        options: { minimumFractionDigits: 0, notation: 'compact' },
                      }),
                      tooltipText: t(
                        customerEngagementranslations.totalCustomerEngagedTooltip.key,
                        customerEngagementranslations.totalCustomerEngagedTooltip.defaultValue,
                      ),
                    },
                    {
                      label: t(
                        customerEngagementranslations.storeTraffic.key,
                        customerEngagementranslations.storeTraffic.defaultValue,
                      ),
                      content: customerEngagementData
                        ? formatValue(customerEngagementData.storeTraffic, {
                            options: { minimumFractionDigits: 0, notation: 'compact' },
                          })
                        : '0',
                      tooltipText: t(
                        customerEngagementranslations.storeTrafficTooltip.key,
                        customerEngagementranslations.storeTrafficTooltip.defaultValue,
                      ),
                    },
                    {
                      label: `${t(
                        customerEngagementranslations.memberRecruited.key,
                        customerEngagementranslations.memberRecruited.defaultValue,
                      )} (%)`,
                      content: (
                        <RowFlexContainer gap={[1]}>
                          <Typography>{customerData.memberRecruited.ratio.toFixed(2)}</Typography>
                          <IconWithText
                            data-testid="member-recruited-icon"
                            label=""
                            iconOptions={{
                              icon: Icons.ARROW,
                              size: 'small',
                            }}
                            onClick={() => {
                              handleSelectedMetric('memberRecruited');
                            }}
                          />
                        </RowFlexContainer>
                      ),
                      tooltipText: t(
                        customerEngagementranslations.memberRecruitedTooltip.key,
                        customerEngagementranslations.memberRecruitedTooltip.defaultValue,
                      ),
                    },
                    {
                      label: `${t(
                        customerEngagementranslations.membersIdentified.key,
                        customerEngagementranslations.membersIdentified.defaultValue,
                      )} (%)`,
                      content: (
                        <RowFlexContainer gap={[1]}>
                          <Typography>{customerData.memberIdentification.conversionRate.toFixed(2)}</Typography>
                          <IconWithText
                            data-testid="member-idenified-icon"
                            label=""
                            iconOptions={{
                              icon: Icons.ARROW,
                              size: 'small',
                            }}
                            onClick={() => {
                              handleSelectedMetric('memberIdentification');
                            }}
                          />
                        </RowFlexContainer>
                      ),
                      tooltipText: t(
                        customerEngagementranslations.memberIdentificationTooltip.key,
                        customerEngagementranslations.memberIdentificationTooltip.defaultValue,
                      ),
                    },
                    {
                      label: `${t(
                        customerEngagementranslations.articleLookups.key,
                        customerEngagementranslations.articleLookups.defaultValue,
                      )} (%)`,
                      content: (
                        <RowFlexContainer gap={[1]}>
                          <Typography>{customerData.articleLookups.conversionRate.toFixed(2)}</Typography>
                          <IconWithText
                            data-testid="article-lookup-icon"
                            label=""
                            iconOptions={{
                              icon: Icons.ARROW,
                              size: 'small',
                            }}
                            onClick={() => {
                              handleSelectedMetric('articleLookups');
                            }}
                          />
                        </RowFlexContainer>
                      ),
                      tooltipText: t(
                        customerEngagementranslations.articleLookupsTooltip.key,
                        customerEngagementranslations.articleLookupsTooltip.defaultValue,
                      ),
                    },
                  ]}
                />
              )}
            </ColumnFlexContainer>
          }
        />
      </ContainerDrawer>
    );
  },
);
